import { useRef, useState } from "react";
import { Plus, Paperclip, CheckCircle2, XCircle, HelpCircle, Send, Pencil, Trash2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { CLIENT_REQUEST_TYPES, clientRequestTypeLabel, clientRequestStatus, QUOTE_STATUSES, fmtEuro, frDate } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const sel = "mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white";

function uploadFile(token, file, onProgress) {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append("file", file);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${api.defaults.baseURL}/review/${token}/upload`);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => (xhr.status < 300 ? resolve(JSON.parse(xhr.responseText)) : reject(new Error(`HTTP ${xhr.status}`)));
    xhr.onerror = () => reject(new Error("network"));
    xhr.send(fd);
  });
}

export function NewRequestButton({ token, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "nouvelle_publication", title: "", explanation: "", desired_date: "", deadline: "", network: "", urgency: "normale", extra: "" });
  const [details, setDetails] = useState({ creation_type: "", dimensions: "", format: "", quantity: "", deadline_wished: "", inspirations: "" });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(false);
  const fileInput = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isUploading = Object.keys(uploading).length > 0;

  const pickFiles = async (fileList) => {
    for (const f of Array.from(fileList)) {
      setUploading((u) => ({ ...u, [f.name]: 0 }));
      try {
        const res = await uploadFile(token, f, (pct) => setUploading((u) => ({ ...u, [f.name]: pct })));
        setFiles((fl) => [...fl, { url: res.url, name: res.name }]);
      } catch { toast.error(`Échec de l'envoi de « ${f.name} » — réessayez`); }
      finally { setUploading((u) => { const n = { ...u }; delete n[f.name]; return n; }); }
    }
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Indiquez un titre"); return; }
    if (isUploading) { toast.error("Envoi de fichier en cours — patientez"); return; }
    setLoading(true);
    try {
      await api.post(`/review/${token}/requests`, {
        ...form,
        desired_date: form.desired_date || null, deadline: form.deadline || null,
        details: form.type === "creation_graphique" ? details : undefined,
        attachments: files,
      });
      toast.success("Demande envoyée à votre agence !");
      setOpen(false);
      setForm({ type: "nouvelle_publication", title: "", explanation: "", desired_date: "", deadline: "", network: "", urgency: "normale", extra: "" });
      setDetails({ creation_type: "", dimensions: "", format: "", quantity: "", deadline_wished: "", inspirations: "" });
      setFiles([]);
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setLoading(false); }
  };

  return (
    <>
      <button data-testid="client-new-request-btn" onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 bg-neutral-900 hover:bg-neutral-800 text-white text-sm font-medium rounded-lg px-4 py-2 transition-colors">
        <Plus className="w-4 h-4" /> Nouvelle demande
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="client-request-modal" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Nouvelle demande</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type de demande</Label>
              <select data-testid="client-request-type" className={sel} value={form.type} onChange={set("type")}>
                {CLIENT_REQUEST_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div><Label>Titre</Label><Input data-testid="client-request-title" className="mt-1.5" value={form.title} onChange={set("title")} placeholder="Ex : Offre de rentrée" /></div>
            <div><Label>Explication</Label><textarea data-testid="client-request-explanation" className={sel} rows={3} value={form.explanation} onChange={set("explanation")} placeholder="Décrivez votre besoin…" /></div>
            {form.type === "creation_graphique" && (
              <div className="border border-violet-200 bg-violet-50/40 rounded-lg p-3 space-y-3" data-testid="client-request-graphisme-details">
                <p className="text-xs font-semibold text-violet-800">Brief création graphique</p>
                <div><Label>Type de création</Label>
                  <select className={sel} value={details.creation_type} onChange={(e) => setDetails({ ...details, creation_type: e.target.value })} data-testid="cr-creation-type">
                    <option value="">—</option>
                    {["Flyer", "Affiche", "Logo", "Brochure", "Carte de visite", "Packaging", "Création digitale", "Bannière", "Autre"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Dimensions</Label><Input className="mt-1.5" placeholder="Ex : A5, 1080×1080…" value={details.dimensions} onChange={(e) => setDetails({ ...details, dimensions: e.target.value })} data-testid="cr-dimensions" /></div>
                  <div><Label>Format de fichier</Label><Input className="mt-1.5" placeholder="PDF, PNG, AI…" value={details.format} onChange={(e) => setDetails({ ...details, format: e.target.value })} data-testid="cr-format" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Quantité de créations</Label><Input type="number" min="1" className="mt-1.5" value={details.quantity} onChange={(e) => setDetails({ ...details, quantity: e.target.value })} data-testid="cr-quantity" /></div>
                  <div><Label>Livraison souhaitée</Label><Input type="date" className="mt-1.5" value={details.deadline_wished} onChange={(e) => setDetails({ ...details, deadline_wished: e.target.value })} data-testid="cr-deadline-wished" /></div>
                </div>
                <div><Label>Inspirations et références</Label><textarea className={sel} rows={2} placeholder="Liens, exemples, intentions…" value={details.inspirations} onChange={(e) => setDetails({ ...details, inspirations: e.target.value })} data-testid="cr-inspirations" /></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date souhaitée</Label><Input data-testid="client-request-date" type="date" className="mt-1.5" value={form.desired_date} onChange={set("desired_date")} /></div>
              <div><Label>Deadline</Label><Input data-testid="client-request-deadline" type="date" className="mt-1.5" value={form.deadline} onChange={set("deadline")} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Réseau concerné</Label>
                <select data-testid="client-request-network" className={sel} value={form.network} onChange={set("network")}>
                  <option value="">—</option>
                  <option value="instagram">Instagram</option><option value="facebook">Facebook</option>
                  <option value="linkedin">LinkedIn</option><option value="tiktok">TikTok</option>
                </select>
              </div>
              <div><Label>Urgence</Label>
                <select data-testid="client-request-urgency" className={sel} value={form.urgency} onChange={set("urgency")}>
                  <option value="normale">Normale</option><option value="urgente">Urgente</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Pièces jointes</Label>
              <input ref={fileInput} type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx,.zip" className="hidden"
                data-testid="client-request-file" onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }} />
              <div onClick={() => fileInput.current?.click()} onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); pickFiles(e.dataTransfer.files); }}
                data-testid="client-request-dropzone"
                className="mt-1.5 border-2 border-dashed border-neutral-300 rounded-lg p-4 text-center text-sm text-neutral-500 hover:border-neutral-500 hover:bg-neutral-50 transition-colors cursor-pointer">
                <Paperclip className="w-4 h-4 mx-auto mb-1 text-neutral-400" />
                Glissez vos fichiers ici ou touchez pour choisir
              </div>
              {Object.entries(uploading).map(([name, pct]) => (
                <p key={name} className="text-xs text-neutral-500 mt-1">Envoi de {name} — {pct} % — ne fermez pas la page</p>
              ))}
              {files.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {files.map((f, i) => <span key={i} className="text-[10px] bg-green-50 text-green-700 rounded px-1.5 py-0.5">{f.name} ✓</span>)}
                </div>
              )}
            </div>
            <div><Label>Informations complémentaires</Label><textarea className={sel} rows={2} value={form.extra} onChange={set("extra")} data-testid="client-request-extra" /></div>
            <Button data-testid="client-request-submit" onClick={submit} disabled={loading || isUploading}
              className="w-full bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5">
              <Send className="w-4 h-4" /> {loading ? "Envoi…" : isUploading ? "Envoi de fichier en cours…" : "Envoyer ma demande"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function QuoteCard({ token, quote, onChanged }) {
  const [question, setQuestion] = useState("");
  const [showQ, setShowQ] = useState(false);
  const [loading, setLoading] = useState(false);
  const st = QUOTE_STATUSES[quote.status] || QUOTE_STATUSES.envoye;

  const answer = async (action) => {
    setLoading(true);
    try {
      await api.post(`/review/${token}/quotes/${quote.id}/answer`, { action });
      toast.success(action === "accepte" ? "Devis accepté — merci !" : "Devis refusé");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  const ask = async () => {
    if (!question.trim()) return;
    await api.post(`/review/${token}/quotes/${quote.id}/question`, { text: question.trim() });
    toast.success("Question envoyée");
    setQuestion(""); setShowQ(false);
  };

  return (
    <div className="border border-neutral-200 rounded-lg p-4 mt-3 bg-neutral-50/50" data-testid={`client-quote-${quote.id}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">
          Devis — {fmtEuro(quote.total_ttc)} {quote.tva_mode === "non_assujetti" ? "HT" : "TTC"}
        </p>
        <span className={`text-[10px] font-medium border rounded-full px-2 py-0.5 ${st.cls}`}>{st.label}</span>
      </div>
      {quote.tva_mode === "non_assujetti" && (
        <p className="text-[10px] text-neutral-400 mt-0.5">TVA non applicable, art. 293 B du CGI</p>
      )}
      <div className="mt-2 space-y-1">
        {(quote.items || []).map((i, k) => (
          <p key={k} className="text-xs text-neutral-600">{i.prestation} — {i.qty} × {fmtEuro(i.price_ht)} HT</p>
        ))}
      </div>
      {quote.delay && <p className="text-xs text-neutral-400 mt-1">Délai annoncé : {quote.delay}</p>}
      {quote.comment && <p className="text-xs text-neutral-500 mt-1 italic">{quote.comment}</p>}
      {quote.status === "envoye" && (
        <div className="flex flex-wrap gap-2 mt-3">
          <Button size="sm" data-testid={`quote-accept-${quote.id}`} onClick={() => answer("accepte")} disabled={loading}
            className="bg-green-600 hover:bg-green-700 text-white gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> Accepter
          </Button>
          <Button size="sm" variant="outline" data-testid={`quote-refuse-${quote.id}`} onClick={() => answer("refuse")} disabled={loading}
            className="text-red-600 gap-1">
            <XCircle className="w-3.5 h-3.5" /> Refuser
          </Button>
          <Button size="sm" variant="outline" data-testid={`quote-question-${quote.id}`} onClick={() => setShowQ(!showQ)} className="gap-1">
            <HelpCircle className="w-3.5 h-3.5" /> Poser une question
          </Button>
        </div>
      )}
      {showQ && (
        <div className="flex gap-2 mt-2">
          <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Votre question…" data-testid={`quote-question-input-${quote.id}`} />
          <Button size="sm" onClick={ask} className="bg-neutral-900 text-white" data-testid={`quote-question-send-${quote.id}`}>Envoyer</Button>
        </div>
      )}
      {(quote.questions || []).map((q) => (
        <p key={q.id} className="text-xs text-blue-700 bg-blue-50 rounded px-2 py-1 mt-1.5">Vous : {q.text}</p>
      ))}
    </div>
  );
}

function EditRequestModal({ token, request, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: request.title, explanation: request.explanation || "",
    desired_date: request.desired_date || "", deadline: request.deadline || "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Indiquez un titre"); return; }
    setLoading(true);
    try {
      await api.patch(`/review/${token}/requests/${request.id}`, {
        title: form.title.trim(), explanation: form.explanation,
        desired_date: form.desired_date || null, deadline: form.deadline || null,
      });
      toast.success("Demande mise à jour");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="client-request-edit-modal" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Modifier ma demande</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Titre</Label><Input data-testid="edit-request-title" className="mt-1.5" value={form.title} onChange={set("title")} /></div>
          <div><Label>Explication</Label><textarea data-testid="edit-request-explanation" className={sel} rows={3} value={form.explanation} onChange={set("explanation")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date souhaitée</Label><Input data-testid="edit-request-date" type="date" className="mt-1.5" value={form.desired_date} onChange={set("desired_date")} /></div>
            <div><Label>Deadline</Label><Input data-testid="edit-request-deadline" type="date" className="mt-1.5" value={form.deadline} onChange={set("deadline")} /></div>
          </div>
          <Button data-testid="edit-request-save" onClick={save} disabled={loading} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white">
            {loading ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ClientRequestsList({ token, requests, quotes, onChanged }) {
  const [editing, setEditing] = useState(null);
  const quoteFor = (rid) => (quotes || []).filter((q) => q.request_id === rid);
  const editable = (r) => !r.locked && !["devis_accepte", "terminee", "annulee"].includes(r.status);

  const remove = async (r) => {
    if (!window.confirm(`Supprimer définitivement la demande « ${r.title} » ?`)) return;
    try {
      await api.delete(`/review/${token}/requests/${r.id}`);
      toast.success("Demande supprimée");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const [showFinales, setShowFinales] = useState(false);
  const nouvelles = requests.filter((r) => r.status === "nouvelle");
  const enCours = requests.filter((r) => ["acceptee", "devis_envoye", "devis_accepte"].includes(r.status));
  const finales = requests.filter((r) => ["terminee", "annulee", "refusee", "devis_refuse"].includes(r.status));

  if (!requests.length) {
    return <p className="text-sm text-neutral-400 text-center py-10" data-testid="client-requests-empty">Aucune demande pour le moment.</p>;
  }

  const renderCard = (r) => {
        const st = clientRequestStatus(r.status);
        return (
          <div key={r.id} className="bg-white border border-neutral-200 rounded-xl shadow-sm p-5" data-testid={`client-request-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-heading font-bold flex-1 min-w-0">{r.title}</h3>
              {r.locked && (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-neutral-500 bg-neutral-100 border border-neutral-200 rounded-full px-2 py-0.5" data-testid={`client-request-locked-${r.id}`}>
                  <Lock className="w-3 h-3" /> Verrouillée (devis validé)
                </span>
              )}
              <span className={`text-xs font-medium border rounded-full px-2.5 py-1 ${st.cls}`}>{st.label}</span>
            </div>
            <p className="text-xs text-neutral-400 mt-1">
              {clientRequestTypeLabel(r.type)}
              {r.desired_date && ` · souhaitée le ${frDate(r.desired_date)}`}
              {r.deadline && ` · deadline ${frDate(r.deadline)}`}
            </p>
            {r.explanation && <p className="text-sm text-neutral-600 mt-2">{r.explanation}</p>}
            {editable(r) && (
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" data-testid={`client-request-edit-${r.id}`} onClick={() => setEditing(r)} className="gap-1.5">
                  <Pencil className="w-3.5 h-3.5" /> Modifier
                </Button>
                <Button size="sm" variant="outline" data-testid={`client-request-delete-${r.id}`} onClick={() => remove(r)} className="gap-1.5 text-red-600">
                  <Trash2 className="w-3.5 h-3.5" /> Supprimer
                </Button>
              </div>
            )}
            {quoteFor(r.id).map((q) => (
              <QuoteCard key={q.id} token={token} quote={q} onChanged={onChanged} />
            ))}
          </div>
        );
  };

  return (
    <div className="space-y-6" data-testid="client-requests-list">
      {nouvelles.length > 0 && (
        <div className="space-y-4" data-testid="client-requests-nouvelles">
          <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-neutral-500">Nouvelles demandes</h3>
          {nouvelles.map(renderCard)}
        </div>
      )}
      {enCours.length > 0 && (
        <div className="space-y-4" data-testid="client-requests-en-cours">
          <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-neutral-500">Demandes en cours</h3>
          {enCours.map(renderCard)}
        </div>
      )}
      {finales.length > 0 && (
        <div data-testid="client-requests-finales">
          <button data-testid="client-requests-toggle-finales" onClick={() => setShowFinales(!showFinales)}
            className="w-full text-xs text-neutral-500 hover:text-neutral-900 border border-dashed border-neutral-300 rounded-lg py-2.5 transition-colors">
            {showFinales ? "Masquer les demandes finalisées" : `Voir toutes les demandes (${finales.length})`}
          </button>
          {showFinales && <div className="space-y-4 mt-4">{finales.map(renderCard)}</div>}
        </div>
      )}
      {editing && (
        <EditRequestModal key={editing.id} token={token} request={editing} onClose={() => setEditing(null)} onSaved={onChanged} />
      )}
    </div>
  );
}
